<template>
  <div>
    <Header />
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <v-icon left color="warning">mdi-alert-box</v-icon>
              Packlisten-Warnungen
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="openRuleDialog(null)">
                <v-icon left>mdi-plus</v-icon>
                Neue Regel
              </v-btn>
            </v-card-title>

            <v-card-text>
              <v-data-table
                :headers="headers"
                :items="rules"
                :loading="loading"
                class="elevation-1"
                :items-per-page="15"
              >
                <template v-slot:item.active="{ item }">
                  <v-switch
                    v-model="item.active"
                    @change="toggleRuleStatus(item)"
                    dense
                    hide-details
                  ></v-switch>
                </template>

                <template v-slot:item.priority="{ item }">
                  <v-chip :color="getPriorityColor(item.priority)" small dark>
                    {{ getPriorityLabel(item.priority) }}
                  </v-chip>
                </template>

                <template v-slot:item.filters="{ item }">
                  <v-chip-group column>
                    <v-chip v-if="item.filter_skus && item.filter_skus.length > 0" x-small>
                      {{ item.filter_skus.length }} SKUs
                    </v-chip>
                    <v-chip v-if="item.filter_countries && item.filter_countries.length > 0" x-small>
                      {{ item.filter_countries.length }} Länder
                    </v-chip>
                    <v-chip v-if="item.filter_categories && item.filter_categories.length > 0" x-small>
                      {{ item.filter_categories.length }} Kategorien
                    </v-chip>
                  </v-chip-group>
                </template>

                <template v-slot:item.stats="{ item }">
                  <div v-if="item.stats">
                    <div class="text-caption">{{ item.stats.total_shows || 0 }} Anzeigen</div>
                    <div class="text-caption text--secondary">{{ item.stats.unique_users || 0 }} Nutzer</div>
                  </div>
                </template>

                <template v-slot:item.created_at="{ item }">
                  {{ formatDate(item.created_at) }}
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn icon small @click="openStatsDialog(item)" color="info" title="Statistiken anzeigen">
                    <v-icon>mdi-chart-bar</v-icon>
                  </v-btn>
                  <v-btn icon small @click="openRuleDialog(item)" color="primary" title="Regel bearbeiten">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small @click="deleteRule(item)" color="error" title="Regel löschen">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Rule Dialog -->
      <PacklistAlertRuleDialog
        v-model="ruleDialog"
        :rule="currentRule"
        @saved="loadRules"
      />

      <!-- Stats Dialog -->
      <PacklistAlertStatsDialog
        v-model="statsDialog"
        :rule="currentRule"
      />
    </v-container>
  </div>
</template>

<script>
import Header from '../header.vue'
import PacklistAlertRuleDialog from './components/PacklistAlertRuleDialog.vue'
import PacklistAlertStatsDialog from './components/PacklistAlertStatsDialog.vue'
import PacklistAlertService from '@/services/PacklistAlertService'

export default {
  name: 'PacklistAlertManager',
  components: {
    Header,
    PacklistAlertRuleDialog,
    PacklistAlertStatsDialog
  },
  data() {
    return {
      loading: false,
      rules: [],
      ruleDialog: false,
      statsDialog: false,
      currentRule: null,
      headers: [
        { text: 'Name', value: 'name', width: '20%' },
        { text: 'Aktiv', value: 'active', width: '10%' },
        { text: 'Priorität', value: 'priority', width: '12%' },
        { text: 'Filter', value: 'filters', width: '18%', sortable: false },
        { text: 'Statistiken', value: 'stats', width: '15%', sortable: false },
        { text: 'Erstellt am', value: 'created_at', width: '15%' },
        { text: 'Aktionen', value: 'actions', width: '10%', sortable: false }
      ]
    }
  },
  created() {
    this.loadRules()
  },
  methods: {
    async loadRules() {
      this.loading = true
      try {
        const response = await PacklistAlertService.getRules()
        this.rules = response.data.rules
      } catch (error) {
        console.error('Error loading rules:', error)
        this.$toast?.error('Fehler beim Laden der Regeln')
      } finally {
        this.loading = false
      }
    },
    openRuleDialog(rule) {
      this.currentRule = rule ? { ...rule } : null
      this.ruleDialog = true
    },
    openStatsDialog(rule) {
      this.currentRule = rule
      this.statsDialog = true
    },
    async toggleRuleStatus(rule) {
      try {
        await PacklistAlertService.updateRule(rule.id, rule)
        this.$toast?.success('Regel aktualisiert')
      } catch (error) {
        console.error('Error updating rule:', error)
        this.$toast?.error('Fehler beim Aktualisieren')
        rule.active = !rule.active
      }
    },
    async deleteRule(rule) {
      if (!confirm(`Regel "${rule.name}" wirklich löschen?`)) return

      try {
        await PacklistAlertService.deleteRule(rule.id)
        this.$toast?.success('Regel gelöscht')
        this.loadRules()
      } catch (error) {
        console.error('Error deleting rule:', error)
        this.$toast?.error('Fehler beim Löschen')
      }
    },
    getPriorityColor(priority) {
      const colors = {
        critical: 'red darken-2',
        high: 'orange darken-1',
        medium: 'blue',
        low: 'grey'
      }
      return colors[priority] || 'grey'
    },
    getPriorityLabel(priority) {
      const labels = {
        critical: 'Kritisch',
        high: 'Hoch',
        medium: 'Mittel',
        low: 'Niedrig'
      }
      return labels[priority] || priority
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.text-caption {
  font-size: 0.75rem;
  line-height: 1.2;
}
</style>
